import React, { useMemo, useEffect } from 'react';
import { useProactiveNudgeRecommendation } from '@jira/ai-rovo-nudge/src/hooks/useProactiveNudgeRecommendation.tsx';
import ChartBarLineIcon from '@atlaskit/icon-lab/core/chart-bar-line';
import { usePublish } from '@atlaskit/rovo-triggers';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useSourceAIActions, useSurfaceAIActions } from '@atlassian/ai-smart-button-api';

export type UseReleasesVersionFilterNudgeOptions = {
	versionId?: string;
	source: string;
};

export const VERSION_NUDGE_TRIGGER = 'ROVO_BUTTON_NUDGE_JIRA_VERSION_VIEWED';

export const VERSION_NUDGE_TRIGGER_EXPERIENCE_ID = 'proactive-jira-version-viewed';

export function useVersionChangedNudge({
	versionId,
	source,
}: UseReleasesVersionFilterNudgeOptions): void {
	const cloudId = useCloudId();

	const isNudgeEnabled = fg('jira_version_details_rovo_button_nudge') && versionId;

	const triggerDetails = useMemo(
		() => (isNudgeEnabled ? { version_ari: versionId } : null),
		[isNudgeEnabled, versionId],
	);

	const { recommendation } = useProactiveNudgeRecommendation(
		VERSION_NUDGE_TRIGGER,
		triggerDetails,
		VERSION_NUDGE_TRIGGER_EXPERIENCE_ID,
	);

	const { setNudges } = useSourceAIActions('jira-ai-rovo-button');
	const { setSurfaceMetadata } = useSurfaceAIActions();
	const publish = usePublish('ai-mate');

	useEffect(() => {
		setSurfaceMetadata({ siteId: cloudId, product: 'jira', contentType: source });
	}, [cloudId, setSurfaceMetadata, source]);

	if (!isNudgeEnabled) return;

	if (!versionId || !recommendation || recommendation.length === 0) {
		return;
	}

	const nudges = recommendation.map((recommendationItem, index) => {
		const iconNode = (
			<ChartBarLineIcon
				size="medium"
				label={recommendationItem.recommendation_details.cta.cta_label}
			/>
		);

		const prompt =
			recommendationItem.recommendation_details.cta.cta_output ??
			recommendationItem.recommendation_details.cta.cta_label;

		let promptWithAlias = prompt;

		if (fg('jira_rovo_button_delivery_manager_alias')) {
			promptWithAlias = `/release ${prompt}`;
		}

		if (fg('jira_rovo_delivery_manager_decomposed_skills_alias')) {
			promptWithAlias = `/summarize-release ${prompt}`;
		}

		return {
			id: `jira-releases-rovo-button-nudge-version-change-${recommendationItem.recommendation_details.context.context_id}`,
			title: recommendationItem.recommendation_details.cta.cta_label,
			description: recommendationItem.recommendation_details.cta.cta_label,
			icon: iconNode,
			nudgeIcon: () => iconNode,
			onClick: () => {
				publish({
					type: 'chat-new',
					product: 'jira',
					source,
					data: {
						name: recommendationItem.recommendation_details.cta.cta_label,
						dialogues: [],
						prompt: promptWithAlias,
					},
				});
			},
			sourceId: `jira-releases-rovo-button-version-${index}`,
			status: 'beta' as const,
		};
	});

	setNudges(nudges);
}
